import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ChangePasswordPayload,
  EditStudentPayload,
  LoginApiResponse,
  LoginRequest,
  Multi_roles_switch,
  MultiRoleApiResponse,
  RegisterUserPayload,
} from "@src/pages/login/types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";

export const LoginApiSlice = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    login: builder.mutation<{ response: LoginApiResponse }, LoginRequest>({
      query: (credentials) => {
        const { isClient, ...rest } = credentials;
        const url = isClient ? `auth/login?isClient=true` : `auth/login`;
        return {
          url,
          method: "POST",
          body: rest,
        };
      },
    }),
    multi_roles: builder.mutation<
      MultiRoleApiResponse,
      { currentPrivilege: string | undefined; userId: string | undefined }
    >({
      query: ({ currentPrivilege, userId }) => ({
        url: `user/update-current-privilege?currentPrivilege=${currentPrivilege}&userId=${userId}`,
        method: "POST",
      }),
    }),
    register: builder.mutation<any, { payload: RegisterUserPayload }>({
      query: ({ payload }) => ({
        url: `user`,
        method: "POST",
        body: payload,
      }),
    }),
    updateStudentProfile: builder.mutation<
      {
        response: string;
      },
      { studentId: string; payload: EditStudentPayload }
    >({
      query: ({ studentId, payload }) => ({
        url: `student/update-student-profile?studentId=${studentId}`,
        method: "PATCH",
        body: payload,
      }),
    }),
    activateAccount: builder.mutation<any, { token: string | undefined }>({
      query: ({ token }) => ({
        url: `user/activation-student/${token}`,
        method: "POST",
      }),
    }),
    resendActivationLink: builder.mutation<any, { userId: string }>({
      query: ({ userId }) => ({
        url: `user/resendActivationLink/${userId}`,
        method: "GET",
      }),
    }),
    forgotPassword: builder.mutation<any, { email: string }>({
      query: ({ email }) => ({
        url: `user/forgot-password/${encodeURIComponent(email)}`,
        method: "POST",
      }),
    }),
    changePassword: builder.mutation<any, { payload: ChangePasswordPayload }>({
      query: ({ payload }) => ({
        url: `user/change-password`,
        method: "POST",
        body: payload,
      }),
    }),
    resetPassword: builder.mutation<
      any,
      { token: string; newPassword: string }
    >({
      query: ({ token, newPassword }) => ({
        url: `user/reset-password/${token}/${encodeURIComponent(newPassword)}`,
        method: "GET",
      }),
    }),
    setPassword: builder.mutation<any, { token: string; password: string }>({
      query: ({ token, password }) => ({
        url: `user/activation/${token}/${encodeURIComponent(password)}`,
        method: "GET",
      }),
    }),

    validateToken: builder.mutation<
      any,
      { token: string; isSetPassword: boolean }
    >({
      query: ({ token, isSetPassword }) => ({
        url: `user/validate-token/${token}/${isSetPassword}`,
        method: "GET",
      }),
    }),

    getCategoryBasedOrg: builder.mutation<any, { category: string }>({
      query: ({ category }) => ({
        url: `organization/get-organization-category?orgCategory=${category}`,
        method: "GET",
      }),
    }),
    saveUser: builder.mutation<any, { user: any; student: any }>({
      query: (payload) => ({
        url: `user/save-user`,
        method: "POST",
        body: payload,
      }),
    }),

    clientLogin: builder.mutation<any, { userEmail: string; password: string }>(
      {
        query: (credentials) => {
          let baseUrl = import.meta.env.VITE_ELECTRON_CLIENT_BASE_URL;

          if (
            typeof window !== "undefined" &&
            !!(window as any).electronApp?.envConfig?.syncApiBaseUrl
          ) {
            let syncApiUrl = (window as any).electronApp.envConfig.syncApiBaseUrl;
            baseUrl = syncApiUrl.endsWith("/") ? `${syncApiUrl}v1/` : `${syncApiUrl}/v1/`;
          }

          return {
            url: `${baseUrl}auth/client-login`,
            method: "POST",
            body: credentials,
          };
        },
      },
    ),
    updateTermsAndConditions: builder.mutation<any, { agreedTermsAndCondition: boolean }>({
      query: ({ agreedTermsAndCondition }) => ({
        url: `user/update-termsAndConditions?agreedTermsAndCondition=${agreedTermsAndCondition}`,
        method: "PATCH",
      }),
    }),
    getAlreadyExistingUsers: builder.query<any, { emailId: string }>({
      query: ({ emailId }) => ({
        url: `user/user-exists-with-role/${encodeURIComponent(emailId)}`,
        method: "GET",
      }),
    }),
    resetSyncTimestamp: builder.mutation<any, { userId: string }>({
      query: ({ userId }) => ({
        url: `../api/sync/reset-sync-timestamp?userId=${userId}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useActivateAccountMutation,
  useResendActivationLinkMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useSetPasswordMutation,
  useChangePasswordMutation,
  useMulti_rolesMutation,
  useValidateTokenMutation,
  useLazyGetAlreadyExistingUsersQuery,
  useGetCategoryBasedOrgMutation,
  useUpdateStudentProfileMutation,
  useSaveUserMutation,
  useClientLoginMutation,
  useUpdateTermsAndConditionsMutation,
  useResetSyncTimestampMutation,
} = LoginApiSlice;
